import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { calculateOptimalPopupPosition, cn } from '../../lib/utils';

export type TooltipPosition = 'top' | 'right' | 'bottom' | 'left' | 'auto';

interface TooltipProps {
  /** The trigger element that will show the tooltip on hover */
  children: React.ReactNode;
  /** The content to show in the tooltip */
  content: React.ReactNode;
  /** Control if tooltip is displayed */
  isOpen?: boolean;
  /** Control tooltip position */
  position?: TooltipPosition;
  /** Optional delay before showing tooltip in ms */
  delay?: number;
  /** Additional classes for the tooltip container */
  className?: string;
  /** Distance from trigger element in pixels */
  offset?: number;
  /** If true, keeps tooltip open on click (toggle behavior) */
  clickToToggle?: boolean;
  /** If true, shows a small arrow pointing to the trigger */
  showArrow?: boolean;
  /** Additional classes for the trigger wrapper */
  triggerClassName?: string;
}

/**
 * A reusable tooltip component that shows content on hover or click
 */
const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  isOpen: controlledIsOpen,
  position = 'auto',
  className = '',
  offset = 8,
  clickToToggle = false,
  showArrow = false,
  triggerClassName = '',
}) => {
  // State for uncontrolled usage
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(false);

  // Use either controlled or uncontrolled state
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : uncontrolledIsOpen;

  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Handle mouseover/mouseout for uncontrolled usage
  const handleMouseOver = (e: React.MouseEvent) => {
    // Always capture this event and stop propagation for proper hover handling
    e.stopPropagation();

    if (controlledIsOpen !== undefined) return; // Skip for controlled component

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Immediately set open for better responsiveness
    setUncontrolledIsOpen(true);
  };

  const handleMouseOut = (e: React.MouseEvent) => {
    // Always capture this event and stop propagation for proper hover handling
    e.stopPropagation();

    if (controlledIsOpen !== undefined) return; // Skip for controlled component

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Close immediately without delay - if needed, we can address tooltip to trigger movement later
    setUncontrolledIsOpen(false);
  };

  const handleClick = () => {
    if (controlledIsOpen !== undefined || !clickToToggle) return; // Skip for controlled component or if not click to toggle
    setUncontrolledIsOpen(!uncontrolledIsOpen);
  };

  // Handle tooltip mouse events for hover persistence
  const handleTooltipMouseOver = () => {
    if (controlledIsOpen !== undefined) return; // Skip for controlled component

    // Clear any pending close timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setUncontrolledIsOpen(true);
  };

  const handleTooltipMouseOut = () => {
    if (controlledIsOpen !== undefined) return; // Skip for controlled component

    // Close immediately
    setUncontrolledIsOpen(false);
  };

  // Close tooltip when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        if (controlledIsOpen === undefined) {
          setUncontrolledIsOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, controlledIsOpen]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Calculate tooltip position
  const getTooltipPosition = () => {
    if (!triggerRef.current) return { top: '0px', left: '0px', opacity: 0 };

    // For initial render when tooltipRef isn't populated yet
    const triggerRect = triggerRef.current.getBoundingClientRect();
    let tooltipWidth = 200; // Default estimated width
    let tooltipHeight = 80; // Default estimated height

    // If tooltip ref is available, use actual dimensions
    if (tooltipRef.current) {
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      tooltipWidth = tooltipRect.width;
      tooltipHeight = tooltipRect.height;
    }

    // For fixed positioning strategies
    if (position !== 'auto') {
      // Basic positioning based on the position prop
      const positions: Record<TooltipPosition, { top: number; left: number }> = {
        top: {
          top: triggerRect.top - tooltipHeight - offset + 20, // Add 20px to move tooltip lower
          left: triggerRect.left + triggerRect.width / 2 - tooltipWidth / 2,
        },
        right: {
          top: triggerRect.top + triggerRect.height / 2 - tooltipHeight / 2,
          left: triggerRect.right + offset,
        },
        bottom: {
          top: triggerRect.bottom + offset,
          left: triggerRect.left + triggerRect.width / 2 - tooltipWidth / 2,
        },
        left: {
          top: triggerRect.top + triggerRect.height / 2 - tooltipHeight / 2,
          left: triggerRect.left - tooltipWidth - offset,
        },
        auto: { top: 0, left: 0 }, // Placeholder, not used
      };

      return {
        top: `${positions[position].top}px`,
        left: `${positions[position].left}px`,
        opacity: 1,
      };
    }

    // For auto positioning, use the calculateOptimalPopupPosition utility
    const calculatedPosition = calculateOptimalPopupPosition(
      { top: triggerRect.bottom, left: triggerRect.left + triggerRect.width / 2 },
      { width: tooltipWidth, height: tooltipHeight },
      { x: 0, y: offset }
    );

    return {
      top: `${calculatedPosition.top}px`,
      left: `${calculatedPosition.left}px`,
      opacity: 1,
    };
  };

  // Function to determine arrow position
  const getArrowPosition = () => {
    if (!showArrow) return {};

    // Arrow positioning based on tooltip position
    let arrowStyles: Record<string, string> = {};

    switch (position) {
      case 'top':
        arrowStyles = {
          bottom: '-5px',
          left: '50%',
          transform: 'translateX(-50%) rotate(45deg)',
          borderBottom: '1px solid rgb(226, 232, 240)',
          borderRight: '1px solid rgb(226, 232, 240)',
        };
        break;
      case 'right':
        arrowStyles = {
          left: '-5px',
          top: '50%',
          transform: 'translateY(-50%) rotate(45deg)',
          borderLeft: '1px solid rgb(226, 232, 240)',
          borderBottom: '1px solid rgb(226, 232, 240)',
        };
        break;
      case 'bottom':
        arrowStyles = {
          top: '-5px',
          left: '50%',
          transform: 'translateX(-50%) rotate(45deg)',
          borderTop: '1px solid rgb(226, 232, 240)',
          borderLeft: '1px solid rgb(226, 232, 240)',
        };
        break;
      case 'left':
        arrowStyles = {
          right: '-5px',
          top: '50%',
          transform: 'translateY(-50%) rotate(45deg)',
          borderTop: '1px solid rgb(226, 232, 240)',
          borderRight: '1px solid rgb(226, 232, 240)',
        };
        break;
      default:
        // Auto positioning doesn't get an arrow as we don't know which side it will appear on
        return {};
    }

    return {
      position: 'absolute',
      width: '10px',
      height: '10px',
      backgroundColor: 'white',
      ...arrowStyles,
    };
  };

  return (
    <div className="inline-block">
      {/* Trigger element */}
      <div
        ref={triggerRef}
        className={cn('inline-block', triggerClassName)}
        onMouseEnter={handleMouseOver}
        onMouseLeave={handleMouseOut}
        onClick={clickToToggle ? handleClick : undefined}
      >
        {children}
      </div>

      {/* Tooltip */}
      {isOpen &&
        createPortal(
          <div
            ref={tooltipRef}
            className={cn(
              'fixed z-[9999] p-2 text-sm bg-white rounded-md shadow-lg border border-gray-200',
              'animate-in fade-in zoom-in-95 duration-150',
              'block',
              className
            )}
            style={{
              ...getTooltipPosition(),
              visibility: 'visible',
              display: 'block',
            }}
            onMouseOver={handleTooltipMouseOver}
            onMouseOut={handleTooltipMouseOut}
          >
            {content}
            {/* Arrow pointing to trigger */}
            {showArrow && <div style={getArrowPosition()} />}
          </div>,
          document.body
        )}
    </div>
  );
};

export default Tooltip;
